/**
 * TaskChainTable Component
 *
 * Displays a table of task chains with expandable tree view of instances.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { ChevronDown, ChevronRight, Clock, Play, RotateCcw } from "lucide-react";

import { dataflowApi } from "@/services/api/dataflow";
import { useDataFlowStore } from "@/stores/dataflowStore";
import type { Instance, TaskChain } from "@/types/dataflow";
import { InstanceRow } from "./InstanceRow";
import { buildTaskChainGroups } from "./runtimeData";

export function TaskChainTable() {
  const {
    expandedTaskChainIds,
    toggleTaskChainExpanded,
    selectedTaskChainId,
    selectedInstanceId,
    setSelectedTaskChain,
    setSelectedInstance,
    setExpandedTaskChainIds,
  } = useDataFlowStore();
  const initializedExpandRef = useRef(false);

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ["dataflow", "batches"],
    queryFn: () => dataflowApi.getTaskChains({ limit: 100 }),
    refetchInterval: 10000,
  });

  const { data: instances = [], isLoading: instancesLoading } = useQuery({
    queryKey: ["dataflow", "instances"],
    queryFn: () => dataflowApi.getInstances(),
    refetchInterval: 10000,
  });

  useEffect(() => {
    if (!initializedExpandRef.current && batches.length > 0) {
      setExpandedTaskChainIds(batches.map((batch) => String(batch.id)));
      initializedExpandRef.current = true;
    }
  }, [batches, setExpandedTaskChainIds]);

  // Build instance tree (flat list for now)
  const getInstanceTree = (instances: Instance[]) => {
    return instances.map((instance) => ({ instance, level: 0, children: [] as TreeNode[] }));
  };

  if (batchesLoading || instancesLoading) {
    return (
      <div className="border border-[var(--border)] rounded-lg bg-[var(--panel)] p-4 text-sm text-[var(--muted)]">
        Loading runtime data...
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="border border-[var(--border)] rounded-lg bg-[var(--panel)] p-4 text-sm text-[var(--muted)]">
        No task chains yet. Run a template to create the first real chain.
      </div>
    );
  }

  return (
    <div className="border border-[var(--border)] rounded-lg overflow-auto bg-[var(--panel)] max-h-full">
      {/* Table Header */}
      <div className="flex items-center gap-4 px-4 py-2.5 bg-[var(--panel-soft)] border-b border-[var(--border)] text-xs font-medium text-[var(--muted)]">
        <span className="w-6"></span>
        <span className="flex-1">Task Chain</span>
        <span className="w-24 text-center">Status</span>
        <span className="w-32 text-center">Progress</span>
        <span className="w-20 text-center">Trigger</span>
      </div>

      {/* Batch Rows */}
      <div className="divide-y divide-[var(--border)]">
        {buildTaskChainGroups(batches, instances).map(({ batch, instances }) => {
          const isExpanded = expandedTaskChainIds.has(String(batch.id));
          const isSelected = selectedTaskChainId === String(batch.id);
          const tree = getInstanceTree(instances);

          return (
            <div key={String(batch.id)} className="">
              {/* Batch Row */}
              <div
                className={`
                  flex items-center gap-4 px-4 py-3 cursor-pointer
                  transition-colors hover:bg-white/[0.03]
                  ${isSelected ? "bg-white/[0.06]" : ""}
                `}
                onClick={() => {
                  toggleTaskChainExpanded(String(batch.id));
                  setSelectedTaskChain(String(batch.id));
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    toggleTaskChainExpanded(String(batch.id));
                    setSelectedTaskChain(String(batch.id));
                  }
                }}
              >
                {/* Expand/Collapse Icon */}
                <button
                  className="flex items-center justify-center w-6 h-6 rounded hover:bg-white/10 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTaskChainExpanded(String(batch.id));
                  }}
                  type="button"
                  aria-label={isExpanded ? "Collapse task chain" : "Expand task chain"}
                >
                  {isExpanded ? (
                    <ChevronDown className="size-4 text-[var(--muted)]" />
                  ) : (
                    <ChevronRight className="size-4 text-[var(--muted)]" />
                  )}
                </button>

                {/* Batch Name */}
                <div className="flex-1 flex items-center gap-3">
                  <BatchIcon trigger={batch.batch_type} status={batch.status} />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-slate-200">
                      {batch.name}
                    </span>
                    <span className="text-[11px] text-[var(--muted)]">
                      task_chain #{batch.id} · {batch.created_at ? formatTime(batch.created_at) : "-"}
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div className="w-24 flex justify-center">
                  <BatchStatusBadge status={batch.status} />
                </div>

                {/* Progress */}
                <div className="w-32">
                  <BatchProgress batch={batch} />
                </div>

                {/* Trigger */}
                <div className="w-20 flex justify-center">
                  <TriggerIcon type={batch.batch_type} />
                </div>
              </div>

              {/* Instance Tree (Expanded) */}
              {isExpanded && (
                <div className="bg-[var(--bg)] border-t border-[var(--border)]">
                  {/* Indentation wrapper for instances */}
                  <div className="pl-4 border-l-2 border-[var(--border)]/50 ml-4 my-1">
                    {renderTreeNodes(tree, selectedInstanceId, setSelectedInstance)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Tree node type for recursive rendering
interface TreeNode {
  instance: Instance;
  level: number;
  children: TreeNode[];
}

function renderTreeNodes(
  nodes: TreeNode[],
  selectedInstanceId: number | null,
  onSelect: (id: number) => void
): React.ReactNode {
  const allNodes: { node: TreeNode; isLast: boolean }[] = [];

  // Flatten tree with last-item tracking
  const flatten = (nodeList: TreeNode[], parentLast = true) => {
    nodeList.forEach((node, index) => {
      const isLast = index === nodeList.length - 1;
      allNodes.push({ node, isLast });
      if (node.children.length > 0) {
        flatten(node.children, isLast);
      }
    });
  };

  flatten(nodes);

  return allNodes.map(({ node, isLast }) => (
    <InstanceRow
      key={node.instance.id}
      instance={node.instance}
      level={node.level}
      isLast={isLast}
      isSelected={selectedInstanceId === node.instance.id}
      onSelect={onSelect}
    />
  ));
}

function BatchIcon({
  trigger,
  status,
}: {
  trigger: TaskChain["batch_type"];
  status: TaskChain["status"];
}) {
  const iconClass = "size-4";

  if (status === "RUNNING") {
    return <RotateCcw className={`${iconClass} text-sky-400 animate-spin`} />;
  }

  if (trigger === "schedule") {
    return <Clock className={`${iconClass} text-purple-400`} />;
  }

  return <Play className={`${iconClass} text-blue-400`} />;
}

function BatchStatusBadge({ status }: { status: TaskChain["status"] }) {
  const colors: Record<string, string> = {
    RUNNING: "bg-sky-500/15 text-sky-400 border-sky-500/30",
    COMPLETED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    FAILED: "bg-rose-500/15 text-rose-400 border-rose-500/30",
    CANCELLED: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    PENDING: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  };

  return (
    <span
      className={`
        inline-flex items-center px-2 py-0.5 text-[11px] font-medium
        rounded-full border ${colors[status]}
      `}
    >
      {status}
    </span>
  );
}

function BatchProgress({ batch }: { batch: TaskChain }) {
  const percent = batch.total_instances > 0
    ? Math.round(((batch.completed_instances + batch.failed_instances) / batch.total_instances) * 100)
    : 0;

  return (
    <div className="flex flex-col gap-1">
      <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
        <div
          className="h-full rounded-full bg-emerald-500 transition-all duration-300"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-[var(--muted)]">
        <span>
          {batch.completed_instances}/{batch.total_instances}
        </span>
        <span>{percent}%</span>
      </div>
    </div>
  );
}

function TriggerIcon({ type }: { type: TaskChain["batch_type"] }) {
  const labels: Record<string, string> = {
    schedule: "SCHED",
    manual: "MANUAL",
    upstream: "UPSTRM",
  };

  const colors: Record<string, string> = {
    schedule: "text-purple-400",
    manual: "text-blue-400",
    upstream: "text-cyan-400",
  };

  return (
    <span className={`text-[11px] font-medium ${colors[type] || "text-[var(--muted)]"}`}>
      {labels[type] || type}
    </span>
  );
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * @deprecated Use TaskChainTable.
 * Backward-compatible alias kept during migration.
 */
export const BatchTable = TaskChainTable;
